import React, { useEffect, useRef, useState } from "react";
import Latex from "react-latex-next";
import { Button } from "react-bootstrap";
import { ZoomImage } from "../ZoomImage/ZoomImage";
import { Question } from "../../types";
import { getDefaultSjtOrderIds } from "./sjtUtils";
import "./SjtAnswerList.scss";

type SjtAnswerListProps = {
  question: Question;
  practiceQuestionAnswered: boolean;
  selection: number[];
  initialLetterByAnswerId: { [key: number]: string };
  delimeters: any[];
  onSelectionChange: (newSelection: number[]) => void;
  onClear: () => void;
  onSubmit: () => void;
  hideSubmitButton?: boolean;
  hideClearButton?: boolean;
};

type PostDropSettle = {
  insertIndex: number;
  movedId: number;
  nextOrder: number[];
};

type RenderItem =
  | { kind: "card"; id: number }
  | { kind: "dropped"; id: number };

const SETTLE_MS = 1000;

const reorder = (list: number[], fromIndex: number, toIndex: number): number[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return list;
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const getEffectiveTargetIndex = (fromIndex: number, insertIndex: number) => {
  let targetIndex = insertIndex;
  if (targetIndex > fromIndex) {
    targetIndex -= 1;
  }
  return targetIndex;
};

const buildSettleRenderItems = (
  order: number[],
  settle: PostDropSettle,
): RenderItem[] => {
  const items: RenderItem[] = [];
  for (let i = 0; i <= order.length; i++) {
    if (i === settle.insertIndex) {
      items.push({ kind: "dropped", id: settle.movedId });
    }
    if (i < order.length) {
      const id = order[i];
      if (id === settle.movedId) continue;
      items.push({ kind: "card", id });
    }
  }
  return items;
};

export const SjtAnswerList: React.FC<SjtAnswerListProps> = ({
  question,
  practiceQuestionAnswered,
  selection,
  initialLetterByAnswerId,
  delimeters,
  onSelectionChange,
  onClear,
  onSubmit,
  hideSubmitButton,
  hideClearButton,
}) => {
  const orderedIds =
    selection.length === question.answerOptions.length
      ? selection
      : getDefaultSjtOrderIds(question);

  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [postDropSettle, setPostDropSettle] = useState<PostDropSettle | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const optionById = new Map(question.answerOptions.map((o) => [o.id, o]));

  const clearSettleTimer = () => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  };

  const commitSettle = (settle: PostDropSettle) => {
    onSelectionChange(settle.nextOrder);
    setPostDropSettle(null);
  };

  useEffect(() => () => clearSettleTimer(), []);

  useEffect(() => {
    if (postDropSettle) return;
    clearSettleTimer();
    setPostDropSettle(null);
  }, [question.id]);

  const clearDragState = () => {
    setDragFromIndex(null);
    setDropIndex(null);
  };

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    if (practiceQuestionAnswered || postDropSettle) return;
    setDragFromIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
    if (e.currentTarget instanceof HTMLElement) {
      e.dataTransfer.setDragImage(e.currentTarget, 24, 24);
    }
  };

  const handleDragOverItem = (index: number) => (e: React.DragEvent) => {
    if (practiceQuestionAnswered || dragFromIndex === null || postDropSettle) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const insertIndex = e.clientY < midY ? index : index + 1;
    setDropIndex(insertIndex);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (
      practiceQuestionAnswered ||
      dragFromIndex === null ||
      dropIndex === null ||
      postDropSettle
    ) {
      clearDragState();
      return;
    }

    const targetIndex = getEffectiveTargetIndex(dragFromIndex, dropIndex);
    if (targetIndex === dragFromIndex) {
      clearDragState();
      return;
    }

    const movedId = orderedIds[dragFromIndex];
    const nextOrder = reorder(orderedIds, dragFromIndex, targetIndex);
    const settle: PostDropSettle = {
      insertIndex: dropIndex,
      movedId,
      nextOrder,
    };

    setPostDropSettle(settle);
    clearDragState();

    clearSettleTimer();
    settleTimerRef.current = setTimeout(() => {
      commitSettle(settle);
      settleTimerRef.current = null;
    }, SETTLE_MS);
  };

  const handleDragEnd = () => {
    if (!postDropSettle) {
      clearDragState();
    }
  };

  const effectiveTargetIndex =
    dragFromIndex !== null && dropIndex !== null
      ? getEffectiveTargetIndex(dragFromIndex, dropIndex)
      : null;

  const showDropGap =
    !postDropSettle &&
    dragFromIndex !== null &&
    dropIndex !== null &&
    effectiveTargetIndex !== dragFromIndex;

  const renderCard = (
    id: number,
    options: {
      isDragging?: boolean;
      isDroppedInSlot?: boolean;
      draggable?: boolean;
      onDragStart?: (e: React.DragEvent) => void;
      onDragOver?: (e: React.DragEvent) => void;
    },
  ) => {
    const answerOption = optionById.get(id);
    if (!answerOption) return null;
    const letter = initialLetterByAnswerId[id];

    return (
      <div
        className={[
          "sjt-answer-list__item",
          practiceQuestionAnswered
            ? "sjt-answer-list__item--locked"
            : "sjt-answer-list__item--draggable",
          options.isDragging ? "sjt-answer-list__item--dragging" : "",
          options.isDroppedInSlot ? "sjt-answer-list__item--dropped-in-slot" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        draggable={options.draggable ?? false}
        onDragStart={options.onDragStart}
        onDragOver={options.onDragOver}
        onDragEnd={handleDragEnd}
      >
        {!practiceQuestionAnswered && (
          <div className="sjt-answer-list__handle" aria-hidden>
            <span />
            <span />
            <span />
          </div>
        )}
        <div className="sjt-answer-list__letter">{letter}</div>
        <div className="sjt-answer-list__body">
          <p>
            <Latex delimiters={delimeters}>{answerOption.text}</Latex>
          </p>
          {answerOption.imageUrl && (
            <ZoomImage src={answerOption.imageUrl} width={180}></ZoomImage>
          )}
        </div>
      </div>
    );
  };

  const renderListContent = () => {
    if (postDropSettle) {
      const settleItems = buildSettleRenderItems(orderedIds, postDropSettle);

      return settleItems.map((item) => {
        if (item.kind === "dropped") {
          return (
            <div
              key={`dropped-${item.id}`}
              className="sjt-answer-list__drop-slot sjt-answer-list__drop-slot--open sjt-answer-list__drop-slot--filled"
            >
              {renderCard(item.id, {
                isDroppedInSlot: true,
              })}
            </div>
          );
        }

        return (
          <React.Fragment key={item.id}>
            {renderCard(item.id, {
              draggable: false,
            })}
          </React.Fragment>
        );
      });
    }

    return orderedIds.map((id, index) => {
      const gapBefore = showDropGap && dropIndex === index;
      const gapAfter =
        showDropGap && dropIndex === orderedIds.length && index === orderedIds.length - 1;

      return (
        <React.Fragment key={id}>
          {gapBefore && (
            <div
              className={[
                "sjt-answer-list__drop-slot",
                showDropGap ? "sjt-answer-list__drop-slot--open" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-hidden
            />
          )}
          {renderCard(id, {
            isDragging: dragFromIndex === index,
            draggable: !practiceQuestionAnswered,
            onDragStart: handleDragStart(index),
            onDragOver: handleDragOverItem(index),
          })}
          {gapAfter && (
            <div
              className={[
                "sjt-answer-list__drop-slot",
                showDropGap ? "sjt-answer-list__drop-slot--open" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-hidden
            />
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="sjt-answer-list">
      {!practiceQuestionAnswered && (
        <p className="sjt-answer-list__hint">
          Drag the options into order — most appropriate at the top, least at the bottom.
          Other cards will part to show where yours will land.
        </p>
      )}

      <div
        className={[
          "sjt-answer-list__list",
          postDropSettle ? "sjt-answer-list__list--settling" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onDragOver={(e) => {
          if (practiceQuestionAnswered || dragFromIndex === null || postDropSettle) return;
          e.preventDefault();
        }}
        onDrop={handleDrop}
        onDragLeave={(e) => {
          if (postDropSettle) return;
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDropIndex(null);
          }
        }}
      >
        {renderListContent()}
      </div>

      {!practiceQuestionAnswered && (
        <div className="sjt-answer-list__actions">
          {hideClearButton ? null : (
            <Button
              variant="outline-secondary"
              onClick={() => {
                clearSettleTimer();
                setPostDropSettle(null);
                onClear();
              }}
              disabled={!!postDropSettle}
            >
              Reset order
            </Button>
          )}
          {hideSubmitButton ? null : (
            <Button onClick={onSubmit} disabled={!!postDropSettle}>
              Submit answer
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
